import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface RevertResult {
    playerId: string;
    name: string;
    eloChange: number;
    clubEloChange: number | null;
    isWinner: boolean;
    currentElo: number;
    eloAfterRevert: number;
}
interface Preview {
    match: {
        id: string; clubId: string | null; isConfirmed: boolean; isActive: boolean;
        winnerId: string | null; teamAScore: number[]; teamBScore: number[];
        startTime: string; teamA: string[]; teamB: string[];
    };
    eligible: boolean;
    results: RevertResult[];
}

export function AdminMatchRevert() {
    const { toast } = useToast();
    const [matchId, setMatchId] = useState("");
    const [preview, setPreview] = useState<Preview | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const previewMutation = useMutation({
        mutationFn: async (id: string) => {
            const res = await apiRequest("GET", `/api/admin/matches/${encodeURIComponent(id)}/revert-preview`);
            return (await res.json()) as Preview;
        },
        onSuccess: (data) => setPreview(data),
        onError: (e: any) => {
            setPreview(null);
            toast({ title: "Aperçu impossible", description: e?.message ?? "Match introuvable", variant: "destructive" });
        },
    });

    const revertMutation = useMutation({
        mutationFn: async (id: string) => {
            const res = await apiRequest("POST", `/api/admin/matches/${encodeURIComponent(id)}/revert-delete`);
            return await res.json();
        },
        onSuccess: (data: any) => {
            toast({ title: "Match annulé", description: `${data.reverted?.length ?? 0} joueurs restaurés` });
            setConfirmOpen(false);
            setPreview(null);
            setMatchId("");
            queryClient.invalidateQueries({ queryKey: ["/api/admin/players"] });
        },
        onError: (e: any) => {
            toast({ title: "Échec de l'annulation", description: e?.message ?? "Erreur serveur", variant: "destructive" });
        },
    });

    const m = preview?.match;

    return (
        <Card>
            <CardHeader>
                <CardTitle>Annuler un match</CardTitle>
                <CardDescription>
                    Restaure l'ELO des joueurs et retire le match de l'historique. À utiliser sur le
                    <strong> dernier match</strong> du club / des joueurs concernés (annulation par soustraction du delta).
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="flex gap-2">
                    <Input
                        placeholder="ID du match (UUID)"
                        value={matchId}
                        onChange={(e) => setMatchId(e.target.value.trim())}
                        onKeyDown={(e) => { if (e.key === "Enter" && matchId) previewMutation.mutate(matchId); }}
                    />
                    <Button
                        variant="secondary"
                        disabled={!matchId || previewMutation.isPending}
                        onClick={() => previewMutation.mutate(matchId)}
                    >
                        {previewMutation.isPending ? "..." : "Aperçu"}
                    </Button>
                </div>

                {m && (
                    <div className="space-y-3 rounded-md border p-3">
                        <div className="flex items-center gap-2 text-sm">
                            <Badge variant={preview!.eligible ? "default" : "destructive"}>
                                {preview!.eligible ? "Éligible" : "Non éligible"}
                            </Badge>
                            <span className="text-gray-500">
                                {new Date(m.startTime).toLocaleString("fr-FR")}
                                {m.clubId ? " · match de club" : " · hors club"}
                            </span>
                        </div>
                        <div className="text-sm">
                            <div><strong>A</strong> ({JSON.stringify(m.teamAScore)}) : {m.teamA.join(" & ")}</div>
                            <div><strong>B</strong> ({JSON.stringify(m.teamBScore)}) : {m.teamB.join(" & ")}</div>
                        </div>

                        {preview!.results.length > 0 ? (
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-gray-500">
                                        <th className="py-1">Joueur</th>
                                        <th>ELO actuel</th>
                                        <th>Δ annulé</th>
                                        <th>ELO après</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {preview!.results.map((r) => (
                                        <tr key={r.playerId} className="border-t">
                                            <td className="py-1">{r.name} {r.isWinner ? "🏆" : ""}</td>
                                            <td>{r.currentElo}</td>
                                            <td className={r.eloChange >= 0 ? "text-green-600" : "text-red-600"}>
                                                {r.eloChange >= 0 ? "+" : ""}{r.eloChange}
                                            </td>
                                            <td className="font-medium">{r.eloAfterRevert}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p className="text-sm text-gray-500">Aucun résultat ELO (match non confirmé ou déjà annulé).</p>
                        )}

                        <Button
                            variant="destructive"
                            disabled={!preview!.eligible || revertMutation.isPending}
                            onClick={() => setConfirmOpen(true)}
                        >
                            Annuler ce match & restaurer les ELO
                        </Button>
                    </div>
                )}
            </CardContent>

            <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Confirmer l'annulation</DialogTitle>
                        <DialogDescription>
                            Cette action est irréversible. L'ELO des {preview?.results.length ?? 0} joueurs sera restauré,
                            le match retiré de l'historique. Assure-toi que c'est bien le dernier match concerné.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmOpen(false)}>Annuler</Button>
                        <Button
                            variant="destructive"
                            disabled={revertMutation.isPending}
                            onClick={() => matchId && revertMutation.mutate(matchId)}
                        >
                            {revertMutation.isPending ? "..." : "Confirmer"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}
